import { Stack } from '@servicetitan/design-system';
import CustomerDetails from '../CustomerDetails';
import CustomerMfeAdapter from '../CustomerMfeAdapter';
import styles from './TaskView.module.css';
import TaskCustomerSearch from '@components/CustomerSearch/TaskCustomerSearch';

import useTasks from '@modules/TasksModule/hooks/useTasks';
import TasksModule from '@modules/TasksModule';
import classNames from 'classnames';
import { useCallback } from 'react';
import TaskLoadingOverlay from '@app/layouts/MainLayout/components/TaskLoadingOverlay/TaskLoadingOverlay';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import useFeatureFlag from '@modules/FeatureFlagModule/hooks/useFeatureFlag/useFeatureFlag';
import maskPhoneNumber from '@lib/utils/maskPhoneNumber';

type Props = {
  taskId: string;
  taskTenantId: string;
  /** Tenant-level OrgNode Guid for taskTenantId. May be absent if the task's tenant isn't in the user's tenant list. */
  taskOrgNodeId?: string;
  show?: boolean;
};

/**
 * This component is used to display the customer search and customer details for a task that has a tenantId
 *
 * @param taskId
 * @param taskTenantId - This is to explicitly pass the tenantId of the task. As this component should not be used without a tenantId.
 * @param taskOrgNodeId - The tenant's OrgNode Guid, resolved by the caller — feeds customer-mfe, which scopes by Guid rather than numeric tenantId.
 * @param show - Whether to show the component. Used to show/hide the component and preserve state.
 */
const TaskView = ({
  taskId,
  show = false,
  taskTenantId,
  taskOrgNodeId,
}: Props) => {
  const { taskById } = useTasks();
  const task = taskById[taskId];
  const isCustomerMfeEnabled = useFeatureFlag(
    FeatureFlagKeys.featureEnableAgentInboxForCalls,
    { defaultValue: false },
  );

  const handleBackClick = useCallback(() => {
    TasksModule.setCustomerOnTask(taskId, undefined);
  }, [taskId]);

  // if the task is not found, in general we should have a task.
  if (!task) {
    return <div>Task not found</div>;
  }

  // we are using 'show' here to toggle the 'display' of the component
  // so it doesn't mount/unmount every time it's selected, because it
  // will lose any state
  return (
    <Stack
      className={classNames('h-100', 'flex-grow-1', {
        'd-b': show,
        [styles.TaskViewShown]: show,
        [styles.TaskViewHidden]: !show,
      })}
    >
      <TaskLoadingOverlay taskId={taskId}>
        <div
          className={classNames('bg-neutral-40', 'h-100', 'of-y-auto', 'd-f', {
            // customer-mfe owns its own internal spacing and should fill this
            // pane edge to edge, unlike the native search/details forms.
            'p-x-3': !isCustomerMfeEnabled,
            'p-y-2': !isCustomerMfeEnabled,
          })}
        >
          {isCustomerMfeEnabled ? (
            <CustomerMfeAdapter
              orgNodeId={taskOrgNodeId}
              customerId={task.attributes.customerId}
              phoneNumber={task.from ? maskPhoneNumber(task.from) : undefined}
              campaignId={task.attributes.campaign?.id}
              callSid={task.attributes.sessionSid}
              onCustomerChanged={(customer) =>
                TasksModule.setCustomerOnTask(taskId, customer)
              }
            />
          ) : (
            <>
              {task.attributes.customerId && (
                <CustomerDetails
                  customerId={task.attributes.customerId}
                  tenantId={taskTenantId}
                  onBackClick={handleBackClick}
                  displayName={task.displayName}
                />
              )}
              <TaskCustomerSearch
                taskId={taskId}
                taskTenantId={taskTenantId}
                show={!task.attributes.customerId}
              />
            </>
          )}
        </div>
      </TaskLoadingOverlay>
    </Stack>
  );
};

export default TaskView;
